/**
 * Assign plan page (Phase 3 Action 37).
 *
 * Form for picking a plan from the catalog + capturing deal metadata
 * (contract URL, MRR, billing cycle, notes). Reads:
 *   - GET /api/plans (catalog)
 *   - GET /api/provisioning/tenants/:id/plan (current assignment, for prefill)
 * Submits to POST /api/provisioning/tenants/:id/plan and navigates
 * back to /fleet/:id.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiPath } from '../api/baseUrl';
import { getStoredToken } from '../auth/AuthContext';

interface Plan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
}

interface DealMetadata {
  contractUrl?: string;
  mrrUsd?: number;
  billingCycle?: 'monthly' | 'annual' | 'other';
  notes?: string;
}

interface PlanAssignment {
  id: string;
  plan: Plan;
  dealMetadata: DealMetadata;
}

async function fetchJson<T>(url: string): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(apiPath(url), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return res.json();
}

async function postAssignment(id: string, body: { planId: string; dealMetadata: DealMetadata }): Promise<void> {
  const token = getStoredToken();
  const res = await fetch(apiPath(`/api/provisioning/tenants/${id}/plan`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `Plan assignment failed: ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
}

export default function AssignPlan() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = '' } = useParams<{ id: string }>();

  const plansQ = useQuery({
    queryKey: ['plans'],
    queryFn: () => fetchJson<{ data: { plans: Plan[] } }>('/api/plans'),
  });

  const currentQ = useQuery({
    queryKey: ['tenant-plan', id],
    queryFn: () => fetchJson<{ data: { assignment: PlanAssignment | null } }>(`/api/provisioning/tenants/${id}/plan`),
    enabled: Boolean(id),
  });

  const [planId, setPlanId] = useState<string>('');
  const [contractUrl, setContractUrl] = useState<string>('');
  const [mrrUsd, setMrrUsd] = useState<string>('');
  const [billingCycle, setBillingCycle] = useState<'' | 'monthly' | 'annual' | 'other'>('');
  const [notes, setNotes] = useState<string>('');

  // Prefill from the current assignment if one exists. Runs once when
  // both queries first land.
  useEffect(() => {
    if (!planId && plansQ.data && currentQ.data) {
      const cur = currentQ.data.data.assignment;
      if (cur) {
        setPlanId(cur.plan.id);
        setContractUrl(cur.dealMetadata.contractUrl ?? '');
        setMrrUsd(cur.dealMetadata.mrrUsd !== undefined ? String(cur.dealMetadata.mrrUsd) : '');
        setBillingCycle(cur.dealMetadata.billingCycle ?? '');
        setNotes(cur.dealMetadata.notes ?? '');
      } else if (plansQ.data.data.plans.length > 0) {
        // Default to the first plan in the catalog.
        setPlanId(plansQ.data.data.plans[0].id);
      }
    }
  }, [plansQ.data, currentQ.data, planId]);

  const mutation = useMutation({
    mutationFn: (body: { planId: string; dealMetadata: DealMetadata }) => postAssignment(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-plan', id] });
      navigate(`/fleet/${id}`);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!planId) return;
    const deal: DealMetadata = {};
    if (contractUrl.trim()) deal.contractUrl = contractUrl.trim();
    if (mrrUsd.trim()) {
      const n = Number(mrrUsd);
      if (Number.isFinite(n) && n >= 0) deal.mrrUsd = n;
    }
    if (billingCycle) deal.billingCycle = billingCycle;
    if (notes.trim()) deal.notes = notes.trim();
    mutation.mutate({ planId, dealMetadata: deal });
  }

  const submitting = mutation.isPending;
  const loading = plansQ.isLoading || currentQ.isLoading;

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <Link to={`/fleet/${id}`} className="text-sm text-slate-500 hover:text-slate-700">← Back to tenant</Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">Assign plan</h1>
        <p className="mt-1 text-sm text-slate-600">
          Provisioning wizard · Step 2 of 5 — Plan
        </p>
      </div>

      {mutation.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {mutation.error.message}
        </div>
      )}

      {loading ? (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading…</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5 rounded-md border border-slate-200 bg-white p-6">
          {/* Plan */}
          <div>
            <label htmlFor="planId" className="block text-sm font-medium text-slate-700">Plan</label>
            <select
              id="planId"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              required
            >
              {plansQ.data?.data.plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.slug})</option>
              ))}
            </select>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h2 className="text-sm font-medium text-slate-700">Deal metadata <span className="text-slate-400">(optional)</span></h2>
            <p className="mt-1 text-xs text-slate-500">Captured for the audit log + future contract review. Visible only to operators.</p>
          </div>

          {/* Contract URL */}
          <div>
            <label htmlFor="contractUrl" className="block text-sm font-medium text-slate-700">Contract URL</label>
            <input
              id="contractUrl"
              type="url"
              value={contractUrl}
              onChange={(e) => setContractUrl(e.target.value)}
              placeholder="https://drive.google.com/…"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* MRR + billing cycle on one row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="mrrUsd" className="block text-sm font-medium text-slate-700">MRR (USD)</label>
              <input
                id="mrrUsd"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={mrrUsd}
                onChange={(e) => setMrrUsd(e.target.value)}
                placeholder="199"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="billingCycle" className="block text-sm font-medium text-slate-700">Billing cycle</label>
              <select
                id="billingCycle"
                value={billingCycle}
                onChange={(e) => setBillingCycle(e.target.value as typeof billingCycle)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">—</option>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-slate-700">Notes</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Sales context, special terms, owner, etc."
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-slate-500">{notes.length}/1000 characters</p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(`/fleet/${id}`)}
              disabled={submitting}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !planId}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save plan'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
